import React, { useEffect, useMemo, useState } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { FaUsers } from "react-icons/fa";

import { getCommunityStats } from "../../../../../services/dashboardServices";

import {
  FaCity,
  FaMapMarkedAlt,
  FaRedo,
  FaSearch,
  FaSortAmountDownAlt,
  FaSortAmountUpAlt,
} from "react-icons/fa";

const COLORS = [
  "#6366F1",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#A855F7",
  "#0EA5E9",
  "#84CC16",
  "#F97316",
  "#EC4899",
  "#14B8A6",
  "#64748B",
];

function extractArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.communes)) return data.communes;
    if (Array.isArray(data.data)) return data.data;
  }

  return [];
}

function normalizeCommune(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("fr-FR");
}

function getPercent(value, total) {
  if (!total) return 0;
  return Math.round((Number(value || 0) / total) * 1000) / 10;
}

function aggregateCommunes(rows) {
  const map = new Map();

  rows.forEach((item) => {
    const commune = normalizeCommune(item?.commune);

    if (!commune) return;

    const current = map.get(commune) || {
      name: commune,
      value: 0,
      codePostal: item?.code_postal || item?.codePostal || null,
      codeInsee: item?.code_insee || item?.codeInsee || null,
      lat: item?.lat || item?.latitude || null,
      lng: item?.lng || item?.longitude || null,
    };

    current.value += Number(item?.count || item?.value || 0);

    map.set(commune, current);
  });

  return Array.from(map.values());
}

function MiniStat({ icon, label, value, helper, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>

        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
            {label}
          </p>

          <p className="mt-0.5 text-xl font-bold text-slate-950 dark:text-white">
            {value}
          </p>

          {helper ? (
            <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">
              {helper}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, total }) {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  const value = Number(row?.value || 0);
  const percent = getPercent(value, total);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 text-sm shadow-xl dark:border-slate-700 dark:bg-slate-900">
      <p className="font-bold text-slate-900 dark:text-white">{row?.name}</p>

      <p className="mt-2 text-slate-700 dark:text-slate-300">
        Adhérents : <span className="font-bold">{formatNumber(value)}</span>
      </p>

      <p className="mt-1 rounded-xl bg-slate-100 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
        {percent}% du total affiché
      </p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="h-5 w-72 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
          <div className="mt-2 h-3 w-96 max-w-full animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        </div>

        <div className="h-10 w-40 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {[...Array(3)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800"
          />
        ))}
      </div>

      <div className="h-[320px] animate-pulse rounded-[24px] bg-slate-200 dark:bg-slate-800" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-8 text-center dark:border-slate-700 dark:bg-slate-950/50">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
        <FaCity />
      </div>

      <h3 className="mt-4 text-lg font-bold text-slate-900 dark:text-white">
        Aucune commune à afficher
      </h3>

      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
        Aucun adhérent de la saison active n’a encore de commune renseignée.
      </p>
    </div>
  );
}

function ErrorPanel({ message, onRetry }) {
  return (
    <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
      <p className="font-bold">Erreur de chargement</p>

      <p className="mt-1 text-sm leading-6">{message}</p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
      >
        <FaRedo />
        Réessayer
      </button>
    </div>
  );
}

export default function CommuneDistributionChart() {
  const [raw, setRaw] = useState([]);
  const [query, setQuery] = useState("");
  const [limitMode, setLimitMode] = useState("top10");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      setErrorMsg("");

      const response = await getCommunityStats();
      const rows = extractArray(response);

      setRaw(aggregateCommunes(rows));
    } catch (error) {
      console.error("Erreur répartition par commune :", error);
      setRaw([]);
      setErrorMsg("Impossible de charger la répartition par commune.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    const rows = q
      ? raw.filter((item) => item.name.toLowerCase().includes(q))
      : [...raw];

    rows.sort((a, b) => {
      if (sortDir === "asc") return a.value - b.value;
      return b.value - a.value;
    });

    return rows;
  }, [raw, query, sortDir]);

  const displayedData = useMemo(() => {
    if (limitMode === "all" || filtered.length <= 10) return filtered;

    const top = filtered.slice(0, 10);
    const othersTotal = filtered
      .slice(10)
      .reduce((sum, item) => sum + Number(item.value || 0), 0);

    return othersTotal > 0
      ? [...top, { name: "Autres communes", value: othersTotal }]
      : top;
  }, [filtered, limitMode]);

  const total = useMemo(() => {
    return displayedData.reduce((sum, item) => sum + Number(item.value || 0), 0);
  }, [displayedData]);

  const globalTotal = useMemo(() => {
    return raw.reduce((sum, item) => sum + Number(item.value || 0), 0);
  }, [raw]);

  const topCommune = filtered[0] || null;

  const hasMapData = useMemo(() => {
    return raw.some((item) => item.lat && item.lng);
  }, [raw]);

  if (loading) return <LoadingSkeleton />;

  if (errorMsg) {
    return <ErrorPanel message={errorMsg} onRetry={fetchData} />;
  }

  if (!raw.length) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
              <FaMapMarkedAlt />
            </span>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Répartition par commune
              </h3>

              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                Analyse des communes renseignées sur les adhérents de la saison
                active.
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <FaRedo className={loading ? "animate-spin" : ""} />
          Actualiser
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <MiniStat
          icon={<FaCity />}
          label="Communes"
          value={formatNumber(raw.length)}
          helper="Communes différentes"
          accent="indigo"
        />

        <MiniStat
          icon={<FaUsers />}
          label="Adhérents"
          value={formatNumber(globalTotal)}
          helper="Total toutes communes"
          accent="emerald"
        />

        <MiniStat
          icon={<FaMapMarkedAlt />}
          label="Commune principale"
          value={topCommune?.name || "—"}
          helper={
            topCommune
              ? `${formatNumber(topCommune.value)} adhérent${
                  topCommune.value > 1 ? "s" : ""
                }`
              : "Aucune donnée"
          }
          accent="amber"
        />
      </div>

      <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/50">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="relative w-full xl:max-w-md">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Rechercher une commune..."
              className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSortDir((current) => (current === "asc" ? "desc" : "asc"))}
              className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              {sortDir === "asc" ? <FaSortAmountUpAlt /> : <FaSortAmountDownAlt />}
              {sortDir === "asc" ? "Croissant" : "Décroissant"}
            </button>

            <button
              type="button"
              onClick={() =>
                setLimitMode((current) => (current === "top10" ? "all" : "top10"))
              }
              className={`rounded-2xl px-3 py-2 text-sm font-bold transition ${
                limitMode === "top10"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              }`}
            >
              {limitMode === "top10" ? "Top 10" : "Tout afficher"}
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,420px)_1fr]">
        <div className="rounded-[24px] border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/60">
          <div className="h-[300px] sm:h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={displayedData}
                  cx="50%"
                  cy="50%"
                  innerRadius="56%"
                  outerRadius="82%"
                  paddingAngle={2}
                  dataKey="value"
                  nameKey="name"
                >
                  {displayedData.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>

                <Tooltip content={<ChartTooltip total={total} />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-2 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
              Total affiché
            </p>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {formatNumber(total)}
            </p>
          </div>
        </div>

        <div className="rounded-[24px] border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950/60">
          <div className="max-h-[390px] overflow-y-auto pr-1">
            <div className="space-y-2">
              {displayedData.map((item, index) => {
                const percent = getPercent(item.value, total);

                return (
                  <div
                    key={item.name}
                    className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="h-3 w-3 shrink-0 rounded-full"
                        style={{ background: COLORS[index % COLORS.length] }}
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                            {item.name}
                          </p>

                          <p className="shrink-0 text-sm font-bold text-slate-900 dark:text-white">
                            {formatNumber(item.value)}
                          </p>
                        </div>

                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${percent}%`,
                              background: COLORS[index % COLORS.length],
                            }}
                          />
                        </div>
                      </div>

                      <span className="w-12 shrink-0 text-right text-xs font-bold text-slate-500 dark:text-slate-400">
                        {percent}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* {!hasMapData ? (
            <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs leading-5 text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
              Carte possible plus tard : il faudra ajouter latitude/longitude ou
              code INSEE pour positionner précisément chaque commune.
            </div>
          ) : null} */}
        </div>
      </div>
    </div>
  );
}